/*
 * enfin levé — v57 reskin, stage 1.
 * Desktop navigation dropdowns + mobile full-screen menu helpers.
 * Vanilla, no dependencies, loaded with `defer` after theme.min.js.
 * It deliberately does NOT touch any Prestige component (Drawer, SearchBar,
 * CartSection); it only drives the new `.ElNav` markup and delegates the
 * mobile drawer to Prestige's own close trigger.
 */
(function () {
  'use strict';

  var CLOSE_DELAY = 120;
  var PANEL_WIDTH = 218;
  var VIEWPORT_GUTTER = 18;
  var DESKTOP_MIN = 1001;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function isDesktop() {
    return window.matchMedia('(min-width: ' + DESKTOP_MIN + 'px)').matches;
  }

  ready(function () {
    syncAnnouncementHeight();
    initNav();
    initMobileMenu();
  });

  /* ------------------------------------------------------------------ *
   * Announcement bar height
   *
   * sections/announcement.liquid sets --announcement-bar-height from
   * offsetHeight, which is rounded to a whole pixel (42 for a bar that is
   * really 42.148px tall). The fixed header is positioned at that value, so
   * the rounding leaves a sub-pixel seam where the black bar meets the
   * header — on some zoom / devicePixelRatio combinations that seam renders
   * as a hairline. getBoundingClientRect() keeps the fraction, so the two
   * edges meet exactly. Nothing else in the theme writes this variable
   * (theme.js never touches it) and nothing keeps it in sync on resize,
   * which also broke the offset whenever the bar re-wrapped.
   * ------------------------------------------------------------------ */

  function syncAnnouncementHeight() {
    var bar = document.getElementById('shopify-section-announcement');
    if (!bar) {
      return;
    }

    var scheduled = false;

    function apply() {
      scheduled = false;
      document.documentElement.style.setProperty('--announcement-bar-height', bar.getBoundingClientRect().height + 'px');
    }

    function schedule() {
      if (scheduled) {
        return;
      }
      scheduled = true;
      window.requestAnimationFrame(apply);
    }

    apply();
    window.addEventListener('load', schedule);
    window.addEventListener('resize', schedule);
  }

  /* ------------------------------------------------------------------ *
   * Desktop dropdowns
   * ------------------------------------------------------------------ */

  function initNav() {
    var nav = document.querySelector('[data-el-nav]');
    if (!nav) {
      return;
    }

    var parents = Array.prototype.slice.call(nav.querySelectorAll('[data-el-nav-parent]'));
    if (!parents.length) {
      return;
    }

    var closeTimer = null;
    var openItem = null;

    function clearTimer() {
      if (closeTimer) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }
    }

    function closeAll() {
      clearTimer();
      parents.forEach(function (item) {
        item.classList.remove('is-open');
        var trigger = item.querySelector('[data-el-nav-trigger]');
        var panel = item.querySelector('[data-el-dropdown]');
        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
        }
        if (panel) {
          panel.setAttribute('aria-hidden', 'true');
          panel.style.removeProperty('transform');
        }
      });
      openItem = null;
    }

    function clampPanel(panel) {
      // The panel is left-aligned with its trigger; nudge it back inside the
      // viewport if that would push it past the right gutter.
      panel.style.removeProperty('transform');
      var rect = panel.getBoundingClientRect();
      var width = rect.width || PANEL_WIDTH;
      var overflow = rect.left + width - (window.innerWidth - VIEWPORT_GUTTER);
      if (overflow > 0) {
        panel.style.transform = 'translateX(' + (-Math.ceil(overflow)) + 'px)';
      }
    }

    function open(item) {
      if (!isDesktop()) {
        return;
      }
      clearTimer();
      if (openItem && openItem !== item) {
        openItem.classList.remove('is-open');
        var prevTrigger = openItem.querySelector('[data-el-nav-trigger]');
        var prevPanel = openItem.querySelector('[data-el-dropdown]');
        if (prevTrigger) {
          prevTrigger.setAttribute('aria-expanded', 'false');
        }
        if (prevPanel) {
          prevPanel.setAttribute('aria-hidden', 'true');
        }
      }

      var trigger = item.querySelector('[data-el-nav-trigger]');
      var panel = item.querySelector('[data-el-dropdown]');
      item.classList.add('is-open');
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
      }
      if (panel) {
        panel.setAttribute('aria-hidden', 'false');
        clampPanel(panel);
      }
      openItem = item;
    }

    function scheduleClose() {
      clearTimer();
      closeTimer = window.setTimeout(closeAll, CLOSE_DELAY);
    }

    parents.forEach(function (item) {
      var trigger = item.querySelector('[data-el-nav-trigger]');
      var panel = item.querySelector('[data-el-dropdown]');

      item.addEventListener('mouseenter', function () {
        open(item);
      });
      item.addEventListener('mouseleave', scheduleClose);

      if (panel) {
        panel.addEventListener('mouseenter', clearTimer);
        panel.addEventListener('mouseleave', scheduleClose);
      }

      if (trigger) {
        trigger.addEventListener('focus', function () {
          open(item);
        });

        trigger.addEventListener('keydown', function (event) {
          if (event.key === 'ArrowDown' || event.keyCode === 40) {
            event.preventDefault();
            open(item);
            var first = panel && panel.querySelector('a');
            if (first) {
              first.focus();
            }
          }
        });
      }

      item.addEventListener('focusout', function (event) {
        if (!event.relatedTarget || !item.contains(event.relatedTarget)) {
          scheduleClose();
        }
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' || event.keyCode === 27) {
        if (openItem) {
          var trigger = openItem.querySelector('[data-el-nav-trigger]');
          closeAll();
          if (trigger) {
            trigger.focus();
          }
        }
      }
    });

    document.addEventListener('pointerdown', function (event) {
      if (!openItem) {
        return;
      }
      if (!openItem.contains(event.target)) {
        closeAll();
      }
    });

    window.addEventListener('scroll', function () {
      if (openItem) {
        closeAll();
      }
    }, { passive: true });

    window.addEventListener('resize', function () {
      if (openItem && !isDesktop()) {
        closeAll();
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Mobile full-screen menu
   * ------------------------------------------------------------------ */

  function initMobileMenu() {
    var drawer = document.getElementById('sidebar-menu');
    if (!drawer) {
      return;
    }

    var closeTrigger = drawer.querySelector('[data-action="close-drawer"][data-drawer-id="sidebar-menu"]');
    var burger = document.querySelector('[data-el-burger]');

    function close() {
      if (closeTrigger) {
        closeTrigger.click();
      }
    }

    // Keep the hamburger's aria-expanded in sync with the drawer state.
    if (burger && window.MutationObserver) {
      var observer = new MutationObserver(function () {
        burger.setAttribute('aria-expanded', drawer.getAttribute('aria-hidden') === 'false' ? 'true' : 'false');
      });
      observer.observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
    }

    // Any navigation or utility action inside the drawer dismisses it.
    drawer.addEventListener('click', function (event) {
      var target = event.target.closest('a, button');
      if (!target || target === closeTrigger) {
        return;
      }
      if (target.hasAttribute('data-action') && target.getAttribute('data-action') === 'close-drawer') {
        return;
      }
      close();
    });

    // Escape closes the mobile drawer (Prestige already does this, this is a
    // safety net for the restyled panel).
    document.addEventListener('keydown', function (event) {
      if ((event.key === 'Escape' || event.keyCode === 27) && drawer.getAttribute('aria-hidden') === 'false') {
        close();
      }
    });
  }
}());

/*
 * enfin levé — v57 VOL 1 (01.09): altimeter rail driver.
 * Ported 1:1 from the prototype (input preview/enfin-leve-merged-site-v57/
 * index.html, rail scroll block). Markup: snippets/enfin-v57-rail.liquid
 * (homepage only, rendered from layout/theme.liquid); styles:
 * enfin-v57.css.liquid §12. Contracts: #railNeedle / #railReadout /
 * #railMobileFill.
 *
 * NOTE 21.09: this module was restored from the prototype after an upload
 * from a stale local mirror overwrote it on the DEV theme.
 */
(function () {
  'use strict';

  var needle = document.getElementById('railNeedle');
  var readout = document.getElementById('railReadout');
  var mobileFill = document.getElementById('railMobileFill');

  // The rail renders on the homepage only (layout/theme.liquid).
  if (!needle || !readout || !mobileFill) {
    return;
  }

  function railTravel() {
    return window.innerHeight - 128;
  }

  function onScroll() {
    var doc = document.documentElement;
    var scrollable = doc.scrollHeight - doc.clientHeight;
    var frac = scrollable > 0 ? Math.min(1, Math.max(0, window.scrollY / scrollable)) : 0;
    var t = 64 + frac * railTravel();
    needle.style.top = t + 'px';
    readout.style.top = t + 'px';
    readout.textContent = String(Math.round(frac * 999)).padStart(3, '0') + 'M';
    mobileFill.style.width = (frac * 100) + '%';
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  onScroll();
}());

/*
 * enfin levé — v57, fix 21.09: uniform hairline weight of the performance
 * bars (.ElPerf .perf-bar, PDP Material block + Fabric Guide).
 *
 * The bars are 1px tall, but the page flow above each entry (image heights
 * from aspect-ratio, fractional line-heights) puts them at fractional
 * document offsets. On a 2x display a 1px line straddling a device-pixel
 * boundary is antialiased across 3 physical pixels and looks visibly
 * bolder than one that lands on the grid — so some fabrics (e.g. SCHOELLER
 * ALLWEATHER) showed "thick" bars while others stayed thin.
 *
 * Fix: after layout settles, nudge every bar onto the device-pixel grid
 * with a sub-pixel translateY. Scrolling moves all entries by the same
 * offset, so the correction stays consistent page-wide.
 */
(function () {
  'use strict';

  var raf = null;

  function snapBars() {
    var bars = document.querySelectorAll('.ElPerf .perf-bar');
    if (!bars.length) {
      return;
    }
    var dpr = window.devicePixelRatio || 1;
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      bar.style.transform = '';
      var top = bar.getBoundingClientRect().top;
      var delta = Math.round(top * dpr) / dpr - top;
      bar.style.transform = Math.abs(delta) > 0.01 ? 'translateY(' + delta.toFixed(3) + 'px)' : '';
    }
  }

  function schedule() {
    if (raf) {
      window.cancelAnimationFrame(raf);
    }
    raf = window.requestAnimationFrame(function () {
      raf = null;
      snapBars();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
  // Re-snap once images/fonts have settled the layout, and on resize.
  window.addEventListener('load', schedule);
  window.addEventListener('resize', schedule);
  if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
    document.fonts.ready.then(schedule);
  }
}());

/*
 * enfin levé - back to top (client QA 05.10). Markup: layout/theme.liquid
 * (button[data-el-to-top]); styles: enfin-v57.css.liquid "Back to top".
 * The CSS only renders the button on phones / touch, so on desktop this just
 * toggles a class on an element that is display:none.
 */
(function () {
  'use strict';

  var btn = document.querySelector('[data-el-to-top]');
  if (!btn) {
    return;
  }

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var visible = false;
  var ticking = false;

  function update() {
    ticking = false;
    var show = (window.pageYOffset || document.documentElement.scrollTop) > window.innerHeight;
    // Theme preview links (?preview_theme_id=) overlay Shopify's preview bar
    // on the bottom of the screen; lift the button above it. Visitors of the
    // live site never have this element.
    btn.classList.toggle('is-preview', !!document.getElementById('PBarNextFrameWrapper'));
    if (show !== visible) {
      visible = show;
      btn.classList.toggle('is-visible', show);
      btn.tabIndex = show ? 0 : -1;
    }
  }

  window.addEventListener('scroll', function () {
    if (!ticking) {
      ticking = true;
      window.requestAnimationFrame(update);
    }
  }, { passive: true });
  window.addEventListener('resize', update);

  btn.addEventListener('click', function () {
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    var skip = document.getElementById('main');
    if (skip) {
      skip.setAttribute('tabindex', '-1');
      skip.focus({ preventScroll: true });
    }
  });

  update();
}());
